import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo } from "react";

interface LocationData {
  states: string[];
  cities: string[];
}

/**
 * Fetches distinct location_state and location_city from job_posts via RPC.
 * Optionally filters cities by a selected state.
 * Pass orgId to scope to a specific org, or statusIn to scope to specific statuses.
 */
export function useJobLocations(opts?: {
  orgId?: string;
  statusIn?: string[];
  selectedState?: string | null;
}) {
  const { orgId, statusIn, selectedState } = opts ?? {};

  const { data: rawLocations } = useQuery({
    queryKey: ["job_locations", orgId, statusIn],
    queryFn: async () => {
      const params: Record<string, any> = {
        org_filter: orgId || null,
      };
      if (statusIn && statusIn.length === 1) {
        params.status_filter = statusIn[0];
      } else if (statusIn && statusIn.length > 1) {
        params.status_filters = statusIn;
      }
      const { data, error } = await (supabase.rpc as any)("get_distinct_job_locations", params);
      if (error) throw error;
      return (data ?? []) as Array<{ location_state: string; location_city: string; job_count: number }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  return useMemo<LocationData>(() => {
    if (!rawLocations) return { states: [], cities: [] };

    const stateSet = new Set<string>();
    const cityList: string[] = [];

    rawLocations.forEach((row) => {
      if (row.location_state) stateSet.add(row.location_state);
      if (row.location_city && (!selectedState || row.location_state === selectedState)) {
        cityList.push(row.location_city);
      }
    });

    return {
      states: Array.from(stateSet).sort(),
      cities: [...new Set(cityList)].sort(),
    };
  }, [rawLocations, selectedState]);
}
